import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { MessageSquare, Phone, Car, DollarSign, Clock, Bot } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { leadService } from '../../services/api';
import { getDisplayName, formatBudget, SOURCE_CONFIG, INTENT_CONFIG } from './leadUtils';

const TAG_COLORS = [
  'bg-violet-100 text-violet-700',
  'bg-cyan-100 text-cyan-700',
  'bg-orange-100 text-orange-700',
  'bg-teal-100 text-teal-700',
  'bg-rose-100 text-rose-700',
];

function getTagColor(tag) {
  return TAG_COLORS[tag.charCodeAt(0) % TAG_COLORS.length];
}

const LeadCard = React.forwardRef(({ lead, onClick, onLeadUpdated, isDragging, style, ...props }, ref) => {
  const [agentActive, setAgentActive] = useState(lead.agent_active ?? false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    setAgentActive(lead.agent_active ?? false);
  }, [lead.id, lead.agent_active]);

  const customer = lead.customer || {};
  const source = SOURCE_CONFIG[lead.source] || SOURCE_CONFIG.manual;
  const SourceIcon = source.icon;
  const displayName = getDisplayName(customer);
  const vehicleLabel =
    lead.vehicleInterest?.make || lead.vehicleInterest?.model
      ? `${lead.vehicleInterest.make || ''} ${lead.vehicleInterest.model || ''}`.trim()
      : null;
  const budgetFormatted = formatBudget(customer.budget);
  const daysAgo = lead.createdAt
    ? formatDistanceToNow(new Date(lead.createdAt), { addSuffix: true, locale: es })
    : '';

  const handleAgentToggle = async (e) => {
    e.stopPropagation();
    if (toggling) return;
    const newValue = !agentActive;
    setAgentActive(newValue);
    setToggling(true);
    try {
      const updated = await leadService.toggleAgent(lead.id, newValue);
      if (onLeadUpdated) onLeadUpdated(updated);
      toast.success(newValue ? 'Agente activado' : 'Agente desactivado');
    } catch {
      setAgentActive(!newValue);
      toast.error('Error al cambiar el agente');
    } finally {
      setToggling(false);
    }
  };

  return (
    <div ref={ref} style={style} {...props}>
      <Card
        className={`cursor-pointer select-none transition-shadow hover:shadow-md ${
          isDragging ? 'shadow-xl rotate-1 opacity-90' : ''
        }`}
        onClick={onClick}
      >
        <CardContent className="p-3 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <p className="font-semibold text-sm text-slate-900 leading-tight">
              {displayName}
            </p>
            <Badge className={`text-xs shrink-0 ${source.badgeColor}`}>
              <SourceIcon className="w-3 h-3 mr-1" />
              {source.label}
            </Badge>
          </div>

          {customer.phone && (
            <div
              className="flex items-center gap-1 text-xs text-slate-500"
              onClick={(e) => e.stopPropagation()}
            >
              <Phone className="w-3 h-3" />
              <a href={`tel:${customer.phone}`} className="hover:text-blue-600 hover:underline">
                {customer.phone}
              </a>
            </div>
          )}

          {vehicleLabel && (
            <div className="flex items-center gap-1 text-xs text-slate-500">
              <Car className="w-3 h-3" />
              <span>{vehicleLabel}</span>
              {lead.vehicleInterest?.year && (
                <span className="text-slate-400">({lead.vehicleInterest.year})</span>
              )}
            </div>
          )}

          {!vehicleLabel && lead.vehicleInterestText && (
            <div className="flex items-center gap-1 text-xs text-slate-500">
              <Car className="w-3 h-3 shrink-0" />
              <span className="truncate">{lead.vehicleInterestText}</span>
            </div>
          )}

          {lead.intent && INTENT_CONFIG[lead.intent] && (
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${INTENT_CONFIG[lead.intent].className}`}
            >
              {INTENT_CONFIG[lead.intent].label}
            </span>
          )}

          {budgetFormatted && (
            <div className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
              <DollarSign className="w-3 h-3" />
              <span>{budgetFormatted}</span>
            </div>
          )}

          {lead.tags && lead.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {lead.tags.map((tag) => (
                <span
                  key={tag}
                  className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${getTagColor(tag)}`}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div
            className="flex items-center justify-between pt-1 border-t border-slate-100"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="flex items-center gap-1 text-[10px] text-slate-400" onClick={(e) => e.stopPropagation()}>
              <Clock className="w-3 h-3" />
              {daysAgo}
            </span>

            <div className="flex items-center gap-2">
              <div
                className={`flex items-center gap-1 text-[10px] font-medium transition-colors ${
                  agentActive ? 'text-violet-600' : 'text-slate-400'
                }`}
                title={agentActive ? 'Agente IA activo' : 'Agente IA inactivo'}
              >
                <Bot className="w-3 h-3" />
                <Switch
                  checked={agentActive}
                  onCheckedChange={() => handleAgentToggle({ stopPropagation: () => {} })}
                  onClick={(e) => e.stopPropagation()}
                  disabled={toggling}
                  className="scale-75 origin-right"
                />
              </div>

              {customer.phone && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-[10px] text-green-600 hover:text-green-700 hover:bg-green-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(
                      `https://wa.me/${customer.phone.replace(/\D/g, '')}`,
                      '_blank'
                    );
                  }}
                >
                  <MessageSquare className="w-3 h-3 mr-1" />
                  WA
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

LeadCard.displayName = 'LeadCard';

export default LeadCard;
